import React from "react";
import { Link } from "react-router-dom";
import { get, orderBy } from "lodash";
import {
    CircularProgress,
    Divider,
    Fade,
    Grid,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import BSInput from "../../../common/js/components/BSInput";
import constants from "./constants";
import { formatDate, toDollarAmount, buildAddress } from "../utils/operations";
import StatusChip from "../../../common/js/components/StatusChip";
import { Sorty, Search } from "../../../common/js/icons";

const PaperSpacing = styled(Paper)(() => ({
    padding: "10px 20px",
    fontSize: 14,
    marginBottom: "15px",
}));

const Sortable = styled("span")(() => ({
    paddingRight: "5px",
    paddingBottom: "3px",
}));
const StyledLink = styled(Link)(() => ({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
    textDecoration: "none",
}));

const FilterInputContainer = styled(Grid)(() => ({
    height: "70px",
    marginBottom: "0px",
    minWidth: "10%",
}));

const StyledDivider = styled(Divider)(() => ({
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    margin: "0 -20px 0 -20px",
}));

const LoadingPlaceholder = styled("div")(() => ({
    marginTop: "3em",
    marginBottom: "1.5em",
    textAlign: "center",
    width: "100%",
}));

const AlternateContent = styled("div")(() => ({
    margin: "auto",
    textAlign: "center",
    marginTop: "10em",
    marginBottom: "3em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
}));

const NoDataCell = styled(TableCell)(() => ({
    borderBottom: "none",
}));

const StyledTabs = styled(Tabs)(() => ({
    borderBottom: "none",
    marginRight: "20px",
    position: "absolute",
    bottom: 0,
    width: "20em",
}));

const StyledTab = styled(Tab)(() => ({
    borderBottom: "none",
    marginRight: "20px",
}));

const NumericHeaderCell = styled(TableCell)(() => ({
    width: "15%",
    paddingRight: "24px",
    paddingTop: "3px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
}));

const NumericCell = styled(TableCell)(() => ({
    paddingRight: "24px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
}));

const LeftColumn = styled(TableCell)(() => ({
    paddingLeft: "0px",
}));

const Transactions = (props) => {
    const {
        columnToSort,
        elementsSaving,
        filterQuery,
        handleFilter,
        handleSort,
        sortDirection,
        switchTab,
        transactions,
        transactionsTab,
    } = props;

    const transactionz = transactions[transactionsTab];

    const data = orderBy(
        filterQuery && transactionz
            ? transactionz.filter((x) => constants.filterByQuery(x, "transactions", filterQuery))
            : transactionz,
        columnToSort,
        sortDirection
    );

    return (
        <PaperSpacing>
            <Grid container justifyContent="space-between">
                <Grid item style={{ position: "relative" }}>
                    <StyledTabs value={transactionsTab} onChange={switchTab}>
                        <StyledTab value="open" label="Open Transactions" />
                        <StyledTab value="closed" label="Closed Transactions" />
                    </StyledTabs>
                </Grid>
                <FilterInputContainer>
                    <BSInput
                        extraInputStyles={{ backgroundColor: "#fafafa", border: "none", marginTop: "10px" }}
                        value={filterQuery}
                        placeholder="Search transactions..."
                        startAdornment={<Search />}
                        startAdornmentStyles={{ color: "#777777" }}
                        onChange={(event) => {
                            handleFilter(event.target.value);
                        }}
                        extraFormControlStyles={{ minWidth: "16em" }}
                    />
                </FilterInputContainer>
            </Grid>
            {get(elementsSaving, `transactions.${transactionsTab}`) ? (
                <LoadingPlaceholder>
                    <CircularProgress color="primary" />
                </LoadingPlaceholder>
            ) : (
                <React.Fragment>
                    <StyledDivider />
                    <Fade in={true}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ width: "28%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center" }}
                                            onClick={() => handleSort("property.propertyAttributes.propertyName")}
                                        >
                                            <Sortable>Property Name</Sortable>
                                            <div>
                                                <Sorty />
                                            </div>
                                        </div>
                                    </TableCell>

                                    <TableCell style={{ width: "15%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center" }}
                                            onClick={() => handleSort("createdBy.person.firstName")}
                                        >
                                            <Sortable>Created By</Sortable>
                                            <div>
                                                <Sorty />
                                            </div>
                                        </div>
                                    </TableCell>

                                    <TableCell style={{ width: "12%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center", position: "relative" }}
                                            onClick={() => handleSort("completionDate")}
                                        >
                                            <Sortable>Completion Date</Sortable>
                                            <div>
                                                <Sorty />
                                            </div>
                                        </div>
                                    </TableCell>

                                    <NumericHeaderCell numeric={true}>
                                        <div onClick={() => handleSort("purchasePrice")}>
                                            <div style={{ display: "inline-block" }}>
                                                <Sortable>Purchase Price</Sortable>
                                                <div style={{ display: "inline-block" }}>
                                                    <Sorty />
                                                </div>
                                            </div>
                                        </div>
                                    </NumericHeaderCell>

                                    <NumericHeaderCell numeric={true}>
                                        <div onClick={() => handleSort("loanAmount")}>
                                            <div style={{ display: "inline-block" }}>
                                                <Sortable>Loan Amount</Sortable>
                                                <div style={{ display: "inline-block" }}>
                                                    <Sorty />
                                                </div>
                                            </div>
                                        </div>
                                    </NumericHeaderCell>

                                    <TableCell style={{ width: "13%", paddingLeft: "5%" }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center", position: "relative" }}
                                            onClick={() => handleSort("status")}
                                        >
                                            <Sortable>Status</Sortable>
                                            <div>
                                                <Sorty />
                                            </div>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            {transactionz && transactionz.length > 0 ? (
                                <TableBody>
                                    {data.map((transaction) => (
                                        <TableRow selectable="false" className="tableRow">
                                            <LeftColumn style={{ padding: 0 }}>
                                                <StyledLink
                                                    to={`/transactions/${get(transaction, "transactionKey", "")}`}
                                                >
                                                    <div>
                                                        {get(
                                                            transaction,
                                                            "property.propertyAttributes.propertyName",
                                                            ""
                                                        )}
                                                    </div>
                                                </StyledLink>
                                                <div>
                                                    {buildAddress(get(transaction, "property.primaryAddress", ""))}
                                                </div>
                                            </LeftColumn>

                                            <TableCell style={{ padding: 0 }}>
                                                {`${get(transaction, "createdBy.person.firstName", "")} ${get(
                                                    transaction,
                                                    "createdBy.person.lastName",
                                                    ""
                                                )}`}
                                            </TableCell>

                                            <TableCell style={{ padding: 0 }}>
                                                {formatDate("MM/DD/YYYY")(get(transaction, "completionDate", ""))}
                                            </TableCell>

                                            <NumericCell numeric>
                                                {toDollarAmount(get(transaction, "purchasePrice", ""))}
                                            </NumericCell>

                                            <NumericCell numeric>
                                                {toDollarAmount(get(transaction, "loanAmount", ""))}
                                            </NumericCell>

                                            <TableCell style={{ paddingLeft: "5%" }}>
                                                <StatusChip status={get(transaction, "status")} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            ) : (
                                <NoDataCell colSpan={6}>
                                    <AlternateContent>No transactions.</AlternateContent>
                                </NoDataCell>
                            )}
                        </Table>
                    </Fade>
                </React.Fragment>
            )}
        </PaperSpacing>
    );
};

export default Transactions;
